import React, { Suspense, lazy, useEffect } from "react";
import apiClient from "../config/apiClient";
import { syncFirebaseAuth } from "../config/firebaseAuthSync";
import { Switch, Route, Redirect } from "react-router-dom";
import { useAuth } from "../components/Auth/AuthContext";
import Loader from "../pages/emailLoader/index";
import ReactGA from 'react-ga4';
import '../GA4Config';
import LoadTop from "../components/Common/ScrollTop/LoadTop";

const Courses = lazy(() => import("../pages/course/list"));
const Course = lazy(() => import("../pages/course/details"));
const Create = lazy(() => import("../pages/course/create"));
const ErrorNoAccess = lazy(() => import("../components/NoAccess/NoAccess"));
const AdminAllCourses = lazy(() => import("../pages/administration/courses"));
const AdminAllUsers = lazy(() => import("../pages/administration/users"));
const AdminAllRequest = lazy(() => import("../pages/administration/requests"));
const AdminAllActivationCodes = lazy(() => import("../pages/administration/activationCodes"));
const AdminAllCategories = lazy(() => import("../pages/administration/categories"));
const Home = lazy(() => import("../pages/home"));
const Login = lazy(() => import("../pages/login"));
const Modify = lazy(() => import("../pages/course/modify"));
const Register = lazy(() => import("../pages/register"));
const Error = lazy(() => import("../pages/404"));
const AdminReportUsers = lazy(() => import("../pages/reports/users"));
const AdminReportCourses = lazy(() => import("../pages/reports/courses"));
const MyAccount = lazy(() => import("../pages/myAccount"));
const ForgotPassword = lazy(() => import("../pages/forgotPassword"));
const PasswordReset = lazy(() => import("../pages/passwordReset"));
const CourseFlow = lazy(() => import("../pages/course/flow/index"));
const Groups = lazy(() => import("../pages/groups/list"));
const Campañas = lazy(() => import("../pages/campaigns/index"));
const Pagos = lazy(() => import("../pages/pagos/index"));
const PagosSuccess = lazy(() => import("../pages/pagos/paymentResult").then((m) => ({ default: m.PagosSuccess })));
const PagosFailure = lazy(() => import("../pages/pagos/paymentResult").then((m) => ({ default: m.PagosFailure })));
const PagosPending = lazy(() => import("../pages/pagos/paymentResult").then((m) => ({ default: m.PagosPending })));
const generalReport = lazy(() => import("../pages/reports/general"));
const AdminEmails = lazy(() => import("../pages/administration/emails"));

const reportUserExit = async (email) => {
    const date = new Date();
    await apiClient.post(`${process.env.REACT_APP_ROOT_URL}/reports/reportUser`,{
        type: 'session_end',
        data: {
            date: `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`,
            time: `${date.getHours()}:${date.getMinutes()}`,
            endTime: Date.now()
        },
        user: email}
    );
}

const reportUserEntry = async (email) => {
    const date = new Date();
    await apiClient.post(`${process.env.REACT_APP_ROOT_URL}/reports/reportUser`,{
        type: 'session_start',
        data: {
            date: `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`,
            time: `${date.getHours()}:${date.getMinutes()}`,
            startTime: Date.now()
        },
        user: email}
    );
}

const App = () => {
    const { userRole, roleLoading } = useAuth();
    const isAdmin = !roleLoading && userRole !== null && userRole !== 'user';
    const isRegularUser = !roleLoading && userRole === 'user';

    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: window.location.pathname
        });
    },[]);

    useEffect(() => {
        if (localStorage.getItem('userProfile')) {
            syncFirebaseAuth();
        }
    }, []);

    useEffect(() => {
        try{
            const token = localStorage.getItem('userProfile');
            if (!token) return;

            const email = JSON.parse(token).token.email;
            reportUserEntry(email);
            const onExit = () => {
                reportUserExit(email);
            };
            window.addEventListener('beforeunload', onExit);
            return () => window.removeEventListener('beforeunload', onExit);
        }
        catch (error) {
            console.error(error.message)
        }
    }, []);

    if (roleLoading) {
        return (
            <div className="App" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p>Cargando...</p>
            </div>
        );
    }

    return (
        <div className="App">
            <LoadTop/>
            <Suspense fallback={<div className="App" style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Cargando...</p></div>}>
            <Switch>
                <Route path="/register" component={Register}/>
                <Route path="/login" component={Login}/>
                <Route path="/forgotPassword" component={ForgotPassword}/>
                <Route path="/resetPassword/:token" component={PasswordReset}/>
                <Route path="/" exact component={Home}/>
                <Route path="/home" component={Home}/>
                <Route path="/cursos" component={Courses}/>
                <Route path="/course/course-single/:id" component={Course}/>
                <Route path="/mis-cursos" component={MyAccount}/>
                <Route path="/course-flow/:id" component={CourseFlow}/>
                <Route path="/pagos" exact component={Pagos}/>
                <Route path="/pagos/success" component={PagosSuccess}/>
                <Route path="/pagos/failure" component={PagosFailure}/>
                <Route path="/pagos/pending" component={PagosPending}/>

                {isAdmin && (
                    <>
                        <Route path="/admin/cursos" component={AdminAllCourses}/>
                        <Route path="/admin/usuarios" component={AdminAllUsers}/>
                        <Route path="/admin/request" component={AdminAllRequest}/>
                        <Route path="/admin/activationCodes" component={AdminAllActivationCodes}/>
                        <Route path="/admin/categories" component={AdminAllCategories}/>
                        <Route path="/admin/emails" component={AdminEmails}/>
                        <Route path='/admin/campañas' component={Campañas}/>
                        <Route path="/groups" component={Groups}/>
                        <Route path="/report-users" component={AdminReportUsers}/>
                        <Route path="/report-courses" component={AdminReportCourses}/>
                        <Route path="/report-general"  component={generalReport}/>
                        <Route path="/create" component={Create}/>
                        <Route path="/modify/:courseId" component={Modify}/>
                        <Route path="/admin/email-loader" component={Loader}/>
                    </>
                )}
                {(isRegularUser || userRole === null) && (
                    <>
                        <Route path="/admin/cursos" component={ErrorNoAccess}/>
                        <Route path="/admin/usuarios" component={ErrorNoAccess}/>
                        <Route path="/admin/request" component={ErrorNoAccess}/>
                        <Route path="/admin/activationCodes" component={ErrorNoAccess}/>
                        <Route path="/admin/categories" component={ErrorNoAccess}/>
                        <Route path="/admin/emails" component={ErrorNoAccess}/>
                        <Route path='/admin/campañas' component={ErrorNoAccess}/>
                        <Route path="/admin/activationCategories" component={ErrorNoAccess}/>
                        <Route path="/groups" component={ErrorNoAccess}/>
                        <Route path="/report-users" component={ErrorNoAccess}/>
                        <Route path="/report-courses" component={ErrorNoAccess}/>
                        <Route path="/report-general"  component={ErrorNoAccess}/>
                        <Route path="/create" component={ErrorNoAccess}/>
                        <Route path="/modify/:courseId" component={ErrorNoAccess}/>
                        <Route path="/admin/email-loader" component={ErrorNoAccess}/>
                    </>
                )}

                <Route path="/404" component={Error}/>
                <Redirect to="/404"/>
            </Switch>
            </Suspense>
        </div>
    );
};

export default App;
